import MuxPlayer from '@mux/mux-player-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Award, BarChart2, BookOpen, ChevronLeft, ChevronRight,
  Database, FileSpreadsheet, Filter, FolderCheck, Kanban, LayoutGrid,
  Phone as PhoneIcon, Radio, Sparkles, Star,
} from 'lucide-react';
import { createContext, useContext, useEffect, useId, useState } from 'react';
import logo from '../logo/lo.png';

// ─── CONFIG — replace before go-live ───────────────────────────────────────────
const PHONE = '9894449002';
const LEAD_FORM_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzux059PkHlzEeSio-Ldu3sU0iEguhHrnkuomhbnjN63KougAfkZoJ-RmvuSCd_pcAtBg/exec'; // appscript/analyst_lead_form.gs
const CRM_LEAD_URL = 'https://vistatech-serversss-production.up.railway.app/api/web-lead'; // the sales CRM gives it to a salesperson (server/routes/crm.js webLead)
const GA4_ID = 'G-3Y3ELRTY00'; // Vista Tech stream (vistatech.org) — scoped to this page only, see useAnalytics()
const CLARITY_ID = 'YOUR_CLARITY_PROJECT_ID'; // Microsoft Clarity project id
const META_PIXEL_ID = '1076380667884015'; // Meta Pixel — scoped to this page only, see useAnalytics()

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    _fbq?: unknown;
  }
}

// ─── COLORS (lifted from the live analyst.vistatech.org design tokens) ────────
const COLOR = {
  flame: '#f85830',
  flameDark: '#de3f19',
  flameWash: '#fff6f2',
  gold: '#d8b820',
  goldWash: '#fdf6dc',
  ink: '#280820',
  inkDeep: '#1c0616',
  inkFaint: '#9b869a',
  inkSoft: '#6a4a61',
  money: '#1e7a4f',
  paper: '#ffffff',
  sand: '#faf5ec',
  rule: 'rgba(40, 8, 32, 0.12)',
};

const FONT_DISPLAY = "'Outfit', system-ui, -apple-system, sans-serif";
const FONT_BODY = "'Figtree', system-ui, -apple-system, sans-serif";
const FONT_TAMIL = "'Noto Sans Tamil', 'Figtree', sans-serif";

// ─── CONTENT (copied verbatim from the live page) ──────────────────────────────
const HERO = {
  badge: 'We guide you end to end till placement.',
  headlineLight: 'Learn analytics in 60 days.',
  headlineBold: 'Pay 50% of your course fee only after you ',
  headlineAccent: 'get the job.',
  primaryCta: { label: 'Start your job training', href: '#enroll' },
};

const PLACEMENT = {
  placedLabel: 'Our students are placed as',
  placed: [
    { name: 'Ajay Krishna', role: 'Junior Business Analyst', pay: '3.4 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241678/Ajay_Krishna_-_powerbi.png' },
    { name: 'Vignesh.', role: 'Business Analyst', pay: '₹22K/month', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241678/vignesh_business_analyst.jpg' },
    { name: 'Rajesh', role: 'Operation Analyst', pay: '3.6 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241676/Rajesh-Operation_analyst.jpg' },
    { name: 'Saravanan M.', role: 'Data Analyst', pay: '3.0 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241678/saravnan_-_data_analyst.jpg' },
    { name: 'Meghana.', role: 'Data Scientist', pay: '4.2 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241677/Meghana-_Data_scientist.jpg' },
    { name: 'Karthik.', role: 'Operation Analyst', pay: '4.0 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241677/karthik-operation_Analyst.jpg' },
    { name: 'Arjun.', role: 'Data Scientist', pay: '4.5 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241677/Arjun-Data_Scientist.jpg' },
    { name: 'Prakash.', role: 'Data Analyst', pay: '4.5 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_140/v1790241678/Rohit_Prakash-Data_anlyst.png' },
  ],
  playbackId: '9WMeNFahYLy4gTYVX43BMRHb8aaj3rhxonUm1mNSNAE',
  posterAlt: 'Can you get the Job in 60 Days? A Vista Tech student talks about how they got placed',
  primaryCta: { label: 'Start your job training', href: '#enroll' },
  tamil: 'தமிழில் கற்றுக்கொள்ளுங்கள்.',
};

const JOURNEY = {
  heading: 'This is how your journey to',
  headingAccent: 'an IT job starts.',
  steps: [
    { title: 'Talk to our consultant', body: 'A mentor calls you, looks at what you have studied so far, and tells you straight whether this program suits you.' },
    { title: 'Enroll and pay half', body: 'Half the fee starts your training. The other half stays where it is until you have a job.' },
    { title: 'Learn the skills', body: 'Excel, SQL, Python, Power BI and Tableau, over 60 days, taught live. Enough to work as a data analyst or a business analyst.' },
    { title: 'Clear the assessment', body: 'Our panel checks you are actually ready before anyone puts you in front of a company. This protects you more than it protects us.' },
    { title: 'Sit for placements', body: 'Our recruitment partners put your profile in front of real openings, not job boards. Minimum 3 LPA.' },
    { title: 'Get placed, then pay the rest', body: 'You have an offer letter and a salary. Only now does the second half of the fee come due.' },
  ],
  summary: { lead: 'Between enrolling and your offer letter,', strong: 'you pay nothing more.' },
};

const CURRICULUM = {
  heading: 'What you learn in',
  headingAccent: '60 days.',
  sub: 'Nine modules, one live class a day, every day accounted for. This is the actual syllabus. Open any module and read what you would be doing that day.',
  countLabel: '9 modules · 60 live classes · 1 capstone project',
  modules: [
    { n: 'Module 1', days: 'Days 1 – 8', title: 'Business analyst foundations', outcome: 'Speak the language of the job: requirements, stakeholders, documentation.', topics: ['Introduction to Business Analytics & the Business Analyst role', '5-step business analysis framework & decision-making process', 'BRD, FRD & SRS documentation', 'SDLC models & the Agile lifecycle', 'Requirement Traceability Matrix (RTM) & business process analysis (As-Is / To-Be)', 'User Acceptance Testing (UAT) & change management', 'SWOT analysis & gap analysis', 'Stakeholder management & requirement gathering techniques'] },
    { n: 'Module 2', days: 'Days 9 – 14', title: 'Excel for analytics', outcome: 'Finish with a working sales dashboard built out of messy data.', topics: ['Excel basics, data types, formatting & tables', 'Functions: SUM, IF, COUNTIF, SUMIF, TEXT, DATE', 'Advanced functions: VLOOKUP, XLOOKUP, INDEX-MATCH', 'Data cleaning, conditional formatting & data validation', 'Pivot tables, pivot charts & slicers', 'Dashboard creation & mini project (sales dashboard)'] },
    { n: 'Module 3', days: 'Days 15 – 20', title: 'SQL for analytics', outcome: 'Pull the exact numbers a manager asked for, out of a real database.', topics: ['Database fundamentals & introduction to SQL', 'SELECT, WHERE, ORDER BY, LIMIT, DISTINCT', 'Aggregate functions, GROUP BY, HAVING & CASE', 'Joins: INNER, LEFT, RIGHT, FULL', 'Subqueries, views & SQL business case studies', 'SQL project: business data analysis'] },
    { n: 'Module 4', days: 'Days 21 – 38', title: 'Python for data analytics', outcome: 'Eighteen days on Python alone, ending at NumPy and pandas.', topics: ['Introduction to Python & installation', 'Python basics: datatypes & operators', 'Conditional statements', 'For loop / while loop', 'Functions', 'Lists: indexing, slicing, methods', 'Tuples & sets: operations, functions, iteration', 'Strings: indexing, slicing, formatting, methods', 'Dictionary: keys, values, methods', 'OOP basics: class, object, constructor', 'OOP advanced: inheritance', 'Polymorphism & abstraction', 'OOP encapsulation & special methods', 'Exception handling: try, except, finally, custom exceptions', 'File handling: read/write txt, csv, json', 'Modules & packages (os, datetime, random)', 'NumPy basics: arrays, operations', 'Pandas basics: Series, DataFrame'] },
    { n: 'Module 5', days: 'Days 39 – 48', title: 'Statistics & exploratory data analysis', outcome: 'Say what the data actually shows, and prove it with a test.', topics: ['Data cleaning & transformation (pivot, merge, group by)', 'Data visualisation with Matplotlib', 'Statistical visualisation with Seaborn', 'Descriptive statistics', 'Probability', 'Hypothesis testing', 'Correlation & regression (linear regression basics)', 'Time series basics: trend and seasonality', 'Exploratory data analysis (EDA): handling large datasets', 'Applied EDA mini project'] },
    { n: 'Module 6', days: 'Days 49 – 53', title: 'Power BI', outcome: 'Publish an interactive dashboard, modelled and written in DAX.', topics: ['Power BI introduction & data import', 'Power Query & data transformation', 'Data modeling & relationships', 'DAX (measures & calculated columns)', 'Visualisations, interactive dashboard & publishing'] },
    { n: 'Module 7', days: 'Days 54 – 55', title: 'Tableau', outcome: 'The second BI tool on your resume, and the one many teams run.', topics: ['Introduction to Tableau: connecting data, basic charts', 'Tableau advanced: interactive dashboards'] },
    { n: 'Module 8', days: 'Days 56 – 59', title: 'BA tools & career prep', outcome: 'Work the way a real team works, then get your resume interview-ready.', topics: ['Jira: agile boards, user stories, sprint planning', 'Trello, Miro & Whimsical for collaboration & process mapping', 'Confluence: documentation & knowledge management', 'AI tools for BAs (ChatGPT, Copilot, NotebookLM, Gamma) + resume & interview prep'] },
    { n: 'Module 9', days: 'Day 60', title: 'Final capstone project', outcome: 'One project that uses everything, and the one you walk into interviews with.', topics: ['Capstone project: BRD + SQL + Python/EDA + Power BI + Jira + presentation'] },
  ],
  toolsLabel: 'The tools you will be working in',
  tools: [
    { name: 'Excel', Icon: FileSpreadsheet, color: '#1e7a4f' },
    { name: 'SQL', Icon: Database, color: '#2563eb' },
    { name: 'Python', Icon: Sparkles, color: '#d8b820' },
    { name: 'Gen AI', Icon: Sparkles, color: '#7c3aed' },
    { name: 'Power BI', Icon: BarChart2, color: '#f2c811' },
    { name: 'Tableau', Icon: BarChart2, color: '#e07b39' },
    { name: 'Business Analytics', Icon: Filter, color: '#f85830' },
    { name: 'Analytics Documentation', Icon: BookOpen, color: '#2563eb' },
    { name: 'Jira', Icon: Kanban, color: '#2563eb' },
    { name: 'Miro', Icon: LayoutGrid, color: '#d8b820' },
  ],
  outcome: { lead: 'At the end you have', strong: 'a finished capstone project, dashboards you can open in an interview, and real tools on your resume.' },
  cta: { label: 'Start your job training', href: '#enroll' },
};

const STARTER_WEEK = {
  badge: 'Not ready to commit yet?',
  headingLight: 'Sit in a full week of live classes',
  headingAccent: 'before you decide.',
  sub: 'One week, seven live classes, the same mentors who teach the 60 days. You see exactly how it is taught before you enrol in anything.',
  embedSrc: 'https://player.mux.com/4ABeWzWiyhZUlhz01q01MRk9S9PZ2rw01rCWYjquf3afsk',
  posterAlt: 'A student talking about the seven day starter week',
  benefits: [
    { Icon: Radio, title: 'Seven live classes', body: 'One a day, in Tamil, with a mentor in the room. Not recordings.' },
    { Icon: FolderCheck, title: 'One finished project', body: 'Built during the week, on real data, ready to show in an interview.' },
    { Icon: Award, title: 'A certificate', body: 'Awarded when you complete the seven days.' },
  ],
  cta: { label: 'Enroll for the one week program', href: '#enroll' },
  note: 'A mentor will talk you through it on the call. No payment now.',
};

const TRUST = {
  sub: 'We place students through recruitment partners, not job boards. Our lead partner alone has 20 years in hiring and over 2,000 placements.',
  stats: [
    { value: '79', suffix: '%', star: true, label: 'of our students placed' },
    { value: '6.7', suffix: ' LPA', star: false, label: 'highest package cracked' },
  ],
  footnote: '* Not a guarantee. This covers students who completed the full program and attended every interview arranged for them.',
  rowOne: ['CBTS', 'Kyndryl', 'TAO Digital', 'Sensiple', 'Prodapt', 'CAPCO', 'Zillion Technologies', 'Fujitsu', 'Contus Tech', 'Digile'],
  rowTwo: ['Orion Systems', 'ManVision', 'Cerebra', 'iOPEX', 'Wipro', 'ZoomInfo', 'Cognizant', 'Changbee', 'Hansiba Technologies', 'Digil Technologies'],
};

const VALUE_STACK = {
  heading: 'What your fee actually',
  headingAccent: 'pays for.',
  sub: 'Priced the way each piece would cost bought on its own, so you can see where the money goes instead of taking our word for it.',
  items: [
    { title: '60 days of live classes', body: 'Excel, SQL, Python, Power BI and Tableau, taught live by a mentor.', value: '₹36,000' },
    { title: 'Your capstone project', body: 'Built on real data, scoped to the job you are going after.', value: '₹6,000' },
    { title: 'A portfolio a recruiter can open', body: 'Your dashboards and queries, arranged so the work speaks first.', value: '₹3,500' },
    { title: 'The panel assessment', body: 'We check you are ready before a company ever sees your profile.', value: '₹4,000' },
    { title: 'Mock interviews, then the real ones', body: 'Practised with a mentor, until the answers stop being a guess.', value: '₹6,000' },
    { title: 'The placement drive', body: 'Our HR partners map your profile to live openings, not job boards.', value: '₹12,000' },
    { title: 'Your resume and LinkedIn, rebuilt', body: 'Written for the analyst roles you are applying to.', value: '₹2,500' },
    { title: 'Recordings of every class', body: 'Miss a day, or sit through a hard one twice. They stay with you.', value: '₹3,000' },
    { title: 'A mentor on WhatsApp', body: 'Stuck at 11pm on a query. Someone answers through the 60 days.', value: '₹3,000' },
    { title: 'Practice files and datasets', body: 'The same Excel files and SQL tables the mentors teach from.', value: '₹1,000' },
    { title: 'Your certificate', body: 'Issued when you complete the program. Goes on your resume.', value: '₹1,500' },
  ],
  totalLabel: 'What that adds up to',
  total: '₹78,500',
  feeLabel: 'Full course fee, everything above included',
  fee: '₹29,999',
  note: 'And you do not pay all of it upfront. How the fee splits is further down.',
  footnote: 'Values are what each part would cost bought on its own. The fee shown is for the group online program. Nothing above is an optional add-on. Every line is included for every student.',
  cta: { label: 'Start your job training', href: '#enroll' },
  secondaryCta: { label: 'See how the payment splits', href: '#fees' },
};

const PRICING = {
  badge: 'No cost until you enrol. No hidden fees.',
  heading: '50% now. The other 50%',
  headingAccent: 'after you are placed.',
  sub: 'Both plans below split the same way. You pay the first half to start training, and the second half only once you have an offer letter in hand, not on a deadline and not on a due date.',
  beats: [
    { step: '1', title: 'Pay the first half', body: 'Your seat, your mentor, your 60 days of classes start.' },
    { step: '2', title: 'Train, then sit for placements', body: 'Our panel clears you, then our partners put you in front of openings.' },
    { step: '3', title: 'Get the offer, then pay the rest', body: 'The second half comes due when there is a salary behind it.' },
  ],
  plans: [
    { id: 'online', name: 'Group · Online', note: 'Live classes, batch of students, from home.', featured: true, featuredLabel: 'Most students pick this', seats: { taken: 23, total: 30 }, total: '₹29,999', now: '₹15,000', points: ['All 60 days live with a mentor', 'Excel, SQL, Python, Power BI, Tableau', 'Capstone project and portfolio', 'Placement drive through our HR partners'] },
    { id: 'offline', name: 'Group · In person', note: 'The same batch, in the Chennai classroom.', featured: false, seats: { taken: 11, total: 20 }, total: '₹39,999', now: '₹20,000', points: ['Everything in the online plan', 'In the room with the mentor', 'Chennai, Egmore'] },
  ],
  labels: { total: 'Full course fee', now: 'To start training', later: 'After you are placed', laterText: 'The remaining 50%', laterNote: 'Due only once you have an offer letter in hand.' },
  assurances: [
    { title: 'Nothing due in between', body: 'From the day you enrol to the day you are placed, you pay nothing more.' },
    { title: 'No cost to find out', body: 'The consultant call is free, and a mentor tells you if this does not suit you.' },
    { title: 'Support does not stop', body: 'Placement support continues until your first offer, however long that takes.' },
  ],
  cta: { label: 'Start your job training', href: '#enroll' },
};

const VIDEO_TESTIMONIALS = {
  heading: 'Hear from people who actually',
  headingAccent: 'got jobs within 60 days.',
  items: [
    { id: 'yamini', name: 'Yamini', role: 'Data Analyst', company: 'Citi', pay: '4.9 LPA', background: '2025 passed out', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240837/yamini-1.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240837/yamini-1.jpg' },
    { id: 'jamuna', name: 'Jamuna', role: 'Technical Analyst', company: 'Citi', pay: '4.9 LPA', background: 'Career gap', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240933/yellow_shirt_girl-1.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240933/yellow_shirt_girl-1.jpg' },
    { id: 'vasikaramani', name: 'Vasikaramani.M', role: 'Data Processing Analyst', company: 'Roop Namyang', pay: '4.2 LPA', background: 'Carrier gap', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790241023/WhatsApp_Video_2026-09-24_at_2.38.03_PM.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790241023/WhatsApp_Video_2026-09-24_at_2.38.03_PM.jpg' },
    { id: 'monish', name: 'Monish', role: 'Business Analyst', company: 'Ifluids Engineering', pay: '3 LPA', background: 'Fresher', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240956/VID_20260923_133449.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240956/VID_20260923_133449.jpg' },
  ],
};

const TEXT_TESTIMONIALS = {
  heading: 'What students say',
  headingAccent: 'about us.',
  sub: 'Straight from the Google reviews. Nothing edited.',
  items: [
    { name: 'Ramya S.', rating: 5, time: '3 weeks ago', text: 'Completed training with internship and got placed as Junior Business Analyst at 3.4 LPA. Thank you Naveen sir for your guidance and mentorship.' },
    { name: 'Dr. Arul Mathi', rating: 5, time: '3 weeks ago', text: "We reached out to Vista Tech for our sister's training. With your guidance she got placed as a Junior Data Analyst within 2 months. Thank you for all your help!" },
    { name: 'Pooja Sivakumar', rating: 5, time: '22 weeks ago', text: 'I completed my Java Full Stack Development course here, and it was an amazing experience. Thanks to their guidance and placement support, I got placed in a good company. Totally recommend this institute!' },
    { name: 'Ganesh Kota', rating: 4, time: '3 months ago', text: 'The trainers are very experienced and explain even complex topics simply. Practical training, real-time projects and excellent placement assistance throughout.' },
    { name: 'Viveka D', rating: 5, time: '22 weeks ago', text: 'I recently completed my Java Full Stack Development course. The course covered Java, Spring Boot, HTML, CSS, JavaScript, and MySQL. This gave me strong technical knowledge and confidence to build complete web applications.' },
    { name: 'Logesh S', rating: 5, time: '3 months ago', text: 'Excellent institute for both learning and placement. Trainers ensure you understand concepts deeply, and the placement support is outstanding.' },
  ],
};

const STUDENTS_TABLE = {
  heading: 'Real students.',
  headingAccent: 'Real companies. Real salaries.',
  sub: 'What they studied before, and what they do now.',
  columns: ['Student', 'Now works as', 'Company', 'Package'],
  students: [
    { name: 'Meghana K.', edu: 'B.Tech IT', role: 'Data Scientist', company: 'Cognizant', salary: '5.0 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241677/Meghana-_Data_scientist.jpg' },
    { name: 'Vignesh R.', edu: 'B.Tech AI/DS', role: 'Business Analyst', company: 'Prodapt', salary: '4.5 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241678/vignesh_business_analyst.jpg' },
    { name: 'Rohit Prakash', edu: 'MCA', role: 'Data Analyst', company: 'Kyndryl', salary: '4.0 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241678/Rohit_Prakash-Data_anlyst.png' },
    { name: 'Saravanan M.', edu: 'B.Tech IT', role: 'Data Analyst', company: 'Kyndryl', salary: '4.0 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241678/saravnan_-_data_analyst.jpg' },
    { name: 'Ajay Krishna', edu: 'BCA', role: 'Power BI Developer', company: 'Hansiba Technologies', salary: '3.8 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241678/Ajay_Krishna_-_powerbi.png' },
    { name: 'Karthik S.', edu: 'B.Sc CS', role: 'Operations Analyst', company: 'CBTS', salary: '3.8 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241677/karthik-operation_Analyst.jpg' },
    { name: 'Arjun', edu: 'B.Tech IT', role: 'Junior Data Scientist', company: 'Changbee', salary: '3.6 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241677/Arjun-Data_Scientist.jpg' },
    { name: 'Rajesh P.', edu: 'BCA', role: 'Operations Analyst', company: 'Contus', salary: '3.5 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241676/Rajesh-Operation_analyst.jpg' },
    { name: 'Vikram N.', edu: 'BE CSE', role: 'Power BI Developer', company: 'Zillion Technologies', salary: '3.5 LPA', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_100/v1790241679/Vikram_-_Powerbi.png' },
    { name: 'Divya R.', edu: 'BCA', role: 'Business Analyst', company: 'Sensiple', salary: '3.5 LPA', photo: null as string | null },
  ],
};

const FAQ = {
  heading: 'Questions people ask',
  headingAccent: 'before they enrol.',
  items: [
    { q: 'What exactly do I pay, and when?', a: '₹15,000 to start your training on the group online plan, and ₹14,999 once you have an offer letter. That is the whole fee: ₹29,999 across both halves, and nothing else. Between those two moments you are not asked for anything, whether that gap is two months or six.' },
    { q: 'What if I finish the course and still do not get placed?', a: 'Then we have not earned the second half, and we do not take it. We do not promise you a job. Nobody honestly can, and you should walk away from anyone who does. What we commit to is the training, the panel assessment and the placement drive, and that the support keeps going until your first offer rather than stopping when the 60 days end.' },
    { q: 'Is the second half really only due after a job?', a: 'Yes, and it is due on your offer letter, not on a date we choose. It is the reason the first half exists: we are betting our own fee on getting you placed, which is not a bet you can make if the training does not work.' },
    { q: 'I am from a non-IT background and have never written code.', a: 'Most of the batch is exactly that: commerce, arts and engineering graduates, and people already working in non-IT jobs. The 60 days start at Excel, which is not programming, and Python arrives only after you have spent a month working with data.' },
    { q: 'What salary can I expect?', a: 'The roles our partners place into start at 3 LPA, and our students have gone in at more than that. What you are offered depends on your interviews, your degree and the company. No institute can quote you a number before it knows any of that.' },
    { q: 'How long after the 60 days until I am placed?', a: 'The placement drive starts as soon as the panel clears you, which is usually within a fortnight of finishing. How long it runs depends on the openings and on how your interviews go. Nothing is payable while you wait.' },
    { q: 'Do I have to quit my job or my college to do this?', a: 'No. Classes are in the evening, after working hours, and every one of them is recorded, so a late shift or an exam week does not cost you the material.' },
    { q: 'Are the classes live, and what language are they in?', a: 'Live, with a mentor you can interrupt and ask questions. Taught in Tamil, with tool names and technical terms kept in English, because that is how they turn up at work and in interviews.' },
    { q: 'Can I see how you teach before I pay for the full program?', a: 'Yes. There is a one-week batch of live classes you can sit in before deciding anything, with the same mentors who teach the 60 days. Ask about it on the consultant call and someone will set it up.' },
    { q: 'What do I need to attend?', a: 'A laptop or desktop and an internet connection that holds a video call. Everything the mentors teach on is free to install, and they tell you what to set up before day one.' },
  ],
};

const CLOSING = {
  heading: 'Sixty days from now, this is either done or it is not started.',
  body: 'The training is live, the placement drive is real, and half the fee waits until you have the job. The only part left is the call.',
  recap: [
    { big: '60 days', small: 'live, with a mentor' },
    { big: '₹15,000', small: 'to start training' },
    { big: 'The rest', small: 'only once you are placed' },
  ],
  cta: { label: 'Start your job training', href: '#enroll' },
  note: 'A mentor calls you within 24 hours. No payment now.',
  trust: ['Placement partner: Infinite HR Consultants', 'Support continues until your first offer', 'No payment to talk to us'],
};

const FOOTER_INFO = {
  line: 'Vista Tech Solutions · IT training and HR consulting.',
  address: 'No.115, 7th Floor, Metro Towers, PH Road, Egmore, Chennai 600084',
  phone: PHONE,
  email: 'support@vistatech.com',
  links: [
    { label: 'Terms', href: '/Terms-and-Conditions' },
    { label: 'Privacy', href: '#privacy' },
  ],
};

const FORM_COPY = {
  heading: 'Tell us where to reach you',
  sub: 'A mentor calls you within 24 hours. No payment now.',
  salaryLabel: 'What salary are you aiming for?',
  salaryOptions: ['3 LPA', '5 LPA', '7 LPA+'],
  submitLabel: 'Get hired in 60 days',
  submitLabelBusy: 'Sending…',
  note: 'Takes 2 minutes. We never share your number.',
  doneHeading: 'Got it.',
  doneBody: 'A mentor will call you within 24 hours. Keep your phone nearby.',
  errorHeading: 'That did not go through.',
  errorBody: 'Check your connection and try again, or call us directly at',
};

const ROSTER = {
  seatsTaken: 23,
  seatsTotal: 30,
  roster: [
    { name: 'Rohit prakash', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_60/v1790241678/Rohit_Prakash-Data_anlyst.png' },
    { name: 'Ajay Krishna', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_60/v1790241678/Ajay_Krishna_-_powerbi.png' },
    { name: 'Vikram', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_60/v1790241679/Vikram_-_Powerbi.png' },
    { name: 'Saravanan M.', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_60/v1790241678/saravnan_-_data_analyst.jpg' },
    { name: 'Rajesh', photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/f_auto,q_auto,w_60/v1790241676/Rajesh-Operation_analyst.jpg' },
  ],
  lines: {
    value: '{taken} of {total} seats taken in this batch',
    starter: 'Only {left} seats left in this batch',
    plan: '{taken} of {total} seats taken',
    closing: '{left} seats left · next batch starts this month',
  },
};

function SeatPill({ line, offset = 0, taken, total, compact = false }: { line: keyof typeof ROSTER.lines; offset?: number; taken?: number; total?: number; compact?: boolean }) {
  const t = taken ?? ROSTER.seatsTaken;
  const tot = total ?? ROSTER.seatsTotal;
  const left = tot - t;
  const text = ROSTER.lines[line].replace('{taken}', String(t)).replace('{total}', String(tot)).replace('{left}', String(left));
  const n = ROSTER.roster.length;
  const avatars = Array.from({ length: 3 }, (_, i) => ROSTER.roster[(offset + i) % n]);
  const avatarSize = compact ? 19 : 22;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: compact ? 7 : 9, maxWidth: '100%',
      padding: compact ? '4px 11px 4px 9px' : '5px 14px 5px 11px', borderRadius: 999,
      background: compact ? 'rgba(30,122,79,.07)' : COLOR.paper, border: `1px solid rgba(30,122,79,${compact ? 0.2 : 0.26})`,
    }}>
      <span aria-hidden="true" style={{ position: 'relative', flexShrink: 0, width: 7, height: 7, borderRadius: '50%', background: COLOR.money }}>
        <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: COLOR.money, animation: 'analyst-pulse 2.4s ease infinite' }} />
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {avatars.map((a, i) => (
          <img key={a.name} src={a.photo} alt="" width={avatarSize} height={avatarSize} style={{ width: avatarSize, height: avatarSize, borderRadius: '50%', objectFit: 'cover', boxShadow: `0 0 0 2px ${COLOR.paper}`, marginLeft: i === 0 ? 0 : -7 }} />
        ))}
      </span>
      <span style={{ fontSize: compact ? 11.5 : 12.5, fontWeight: 600, lineHeight: 1.35, color: COLOR.inkSoft, fontVariantNumeric: 'tabular-nums' }}>{text}</span>
    </span>
  );
}

const accentUnderline: React.CSSProperties = {
  color: COLOR.ink,
  fontWeight: 600,
  backgroundImage: `linear-gradient(${COLOR.gold}, ${COLOR.gold})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '0 92%',
  backgroundSize: '100% 0.11em',
};

// ─── SEO ────────────────────────────────────────────────────────────────────────
function useSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Learn analytics in 60 days · Pay half after you get the job | Vista Tech';

    const upsert = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [, attrName, attrVal] = sel.match(/\[([a-z]+)="([^"]+)"\]/) || [];
        if (attrName && attrVal) el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };

    upsert('meta[name="description"]', 'content', 'Vista Tech trains you in full stack analytics in 60 days, in Tamil, in Chennai. Pay 15,000 to start and the rest only after you are placed. Try one week of live classes for 1,000.');
    upsert('meta[property="og:title"]', 'content', 'Learn analytics in 60 days. Pay half only after you get the job.');
    upsert('meta[property="og:description"]', 'content', 'Live classes in Tamil. 60 days. Half your fee waits until you are placed. Try one week for 1,000.');
    upsert('meta[property="og:type"]', 'content', 'website');

    if (!document.getElementById('analyst-google-fonts')) {
      const link = document.createElement('link');
      link.id = 'analyst-google-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600&family=Figtree:wght@400;500;600;700&family=Noto+Sans+Tamil:wght@400;600&display=swap';
      document.head.appendChild(link);
    }

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'analyst-ld';
    ld.textContent = JSON.stringify([
      { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQ.items.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
      { '@context': 'https://schema.org', '@type': 'Organization', name: 'Vista Tech Solutions', url: 'https://vistatech.org' },
    ]);
    document.head.appendChild(ld);

    return () => { document.title = prevTitle; document.getElementById('analyst-ld')?.remove(); };
  }, []);
}

// ─── ANALYTICS (Google Analytics 4 + Microsoft Clarity) ─────────────────────────
function useAnalytics() {
  // Loads gtag.js only while this page is mounted, and tears it fully back down on
  // unmount (script tag + dataLayer + window.gtag) so navigating elsewhere in the
  // SPA can't keep reporting page views under this stream. GA4's own "page changes
  // based on browser history events" Enhanced Measurement setting still runs
  // independently of this cleanup — turn that toggle off for the Vista Tech stream
  // (Admin → Data Streams → Vista Tech → gear icon) if you want a hard guarantee
  // that only this page ever reports in, even mid-session.
  useEffect(() => {
    if (!GA4_ID || GA4_ID.startsWith('YOUR_')) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, { page_path: window.location.pathname, send_page_view: true });

    let script = document.querySelector<HTMLScriptElement>(`script[src*="googletagmanager.com/gtag/js"]`);
    const createdHere = !script;
    if (!script) {
      script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      document.head.appendChild(script);
    }

    return () => {
      if (createdHere) script?.remove();
      delete (window as any).gtag;
      delete (window as any).dataLayer;
    };
  }, []);

  useEffect(() => {
    if (!CLARITY_ID || CLARITY_ID.startsWith('YOUR_')) return;
    if (document.getElementById('ms-clarity')) return;

    (function (win: Window, doc: Document, tagName: string, projectId: string) {
      const c = win as any;
      c.clarity = c.clarity || function (...args: any[]) { (c.clarity.q = c.clarity.q || []).push(args); };
      const script = doc.createElement(tagName);
      script.setAttribute('id', 'ms-clarity');
      (script as HTMLScriptElement).async = true;
      (script as HTMLScriptElement).src = `https://www.clarity.ms/tag/${projectId}`;
      const first = doc.getElementsByTagName(tagName)[0];
      first.parentNode?.insertBefore(script, first);
    })(window, document, 'script', CLARITY_ID);
  }, []);

  // Meta Pixel — same teardown-on-unmount treatment as GA4 above, so this page's
  // PageView/Lead events don't keep attaching to fbq once you navigate elsewhere.
  useEffect(() => {
    if (!META_PIXEL_ID || META_PIXEL_ID.startsWith('YOUR_')) return;

    let script = document.querySelector<HTMLScriptElement>('script[src*="connect.facebook.net"]');
    const createdHere = !script;

    // kept as a local `any` (not window.fbq directly) so TS doesn't need to keep
    // narrowing an optional global property across the calls below.
    const fbq: any = window.fbq || function (...args: any[]) {
      fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
    };
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = fbq.queue || [];

    if (!script) {
      script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }

    fbq('init', META_PIXEL_ID);
    fbq('track', 'PageView');

    return () => {
      if (createdHere) script?.remove();
      delete (window as any).fbq;
      delete (window as any)._fbq;
    };
  }, []);
}

// track a lead-form submission in GA4, Clarity, and the Meta Pixel, mirroring the
// live site's fbq('track','Lead') call at the same moment — one submit, three tools.
function trackLead(section: string) {
  if (typeof window.gtag === 'function') window.gtag('event', 'generate_lead', { section });
  if (typeof window.clarity === 'function') window.clarity('event', 'lead_submitted');
  if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { section });
}

// ─── MODAL CONTEXT ──────────────────────────────────────────────────────────────
const ModalCtx = createContext<{ open: (source: string) => void }>({ open: () => {} });

// ─── SMALL PIECES ───────────────────────────────────────────────────────────────
function CTAButton({ href, children, variant = 'primary', small = false }: { href: string; children: React.ReactNode; variant?: 'primary' | 'secondary'; small?: boolean }) {
  const { open } = useContext(ModalCtx);
  const isEnroll = href === '#enroll';
  const isCall = href === '#call';

  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
    fontFamily: FONT_BODY, fontWeight: 700, fontSize: small ? 14.5 : 16,
    minHeight: small ? 44 : 54, padding: small ? '0 20px' : '0 28px', lineHeight: 1,
    borderRadius: 999, cursor: 'pointer',
    border: variant === 'primary' ? 'none' : `1.5px solid ${COLOR.rule}`,
    background: variant === 'primary' ? COLOR.flame : COLOR.paper,
    color: variant === 'primary' ? '#fff' : COLOR.ink,
    textDecoration: 'none', transition: 'transform .16s var(--ease,ease), box-shadow .16s ease, background .16s ease, border-color .16s ease',
    boxShadow: variant === 'primary' && !small ? '0 14px 28px -12px rgba(248,88,48,0.7)' : 'none',
    whiteSpace: 'nowrap',
  };

  if (isEnroll) {
    return (
      <button type="button" style={base} onClick={() => open('cta')}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
        {children}
      </button>
    );
  }
  return (
    <a href={isCall ? `tel:${PHONE}` : href} style={base}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
      {children}
    </a>
  );
}

const AVATAR_PALETTE = ['#fbdcd0', '#e6d8f5', '#d3ecdb', '#f5dbe4', '#d3e4f5', '#f5edc4'];

function Avatar({ src, name, bg }: { src?: string | null; name: string; bg?: string }) {
  if (src) return <img src={src} alt={name} width={48} height={48} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  return (
    <span style={{ width: 48, height: 48, borderRadius: '50%', background: bg || COLOR.goldWash, color: COLOR.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }} aria-hidden="true">
      {name.trim().charAt(0)}
    </span>
  );
}

function StarRow({ rating, color = COLOR.gold }: { rating: number; color?: string }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2 }} aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={14} style={{ color, fill: i <= rating ? color : 'none' }} aria-hidden="true" />
      ))}
    </span>
  );
}

// mirrors the live site's .heading rule: font-weight 300, a ch-based max-width, and a
// fluid clamp() size; only Closing/hero-like moments use the bold "lg" variant.
function SectionHeading({ children, maxWidthCh = 20, size = 'md' }: { children: React.ReactNode; maxWidthCh?: number; size?: 'md' | 'lg' }) {
  const fontSize = size === 'lg' ? 'clamp(28px, 4.6vw, 44px)' : 'clamp(27px, 4.4vw, 42px)';
  const letterSpacing = size === 'lg' ? '-0.03em' : '-0.025em';
  return (
    <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 300, color: COLOR.ink, fontSize, letterSpacing, lineHeight: 1.12, margin: '0 auto', maxWidth: `${maxWidthCh}ch`, textAlign: 'center' }}>
      {children}
    </h2>
  );
}

function SectionSub({ children, maxWidthCh = 50 }: { children: React.ReactNode; maxWidthCh?: number }) {
  return <p style={{ maxWidth: `${maxWidthCh}ch`, margin: '14px auto 0', fontSize: 'clamp(15px, 1.8vw, 16.5px)', color: COLOR.inkSoft, textAlign: 'center', lineHeight: 1.5 }}>{children}</p>;
}

const sectionHead: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' };

const container: React.CSSProperties = { maxWidth: 1140, margin: '0 auto', padding: '0 20px' };

// ─── LEAD FORM ──────────────────────────────────────────────────────────────────
function validateLead(f: { name: string; phone: string; email: string; salary: string }) {
  const errs: Record<string, string> = {};
  if (!f.name.trim()) errs.name = 'Please tell us your name.';
  const digits = f.phone.replace(/\D/g, '');
  if (!digits) errs.phone = 'We need a number to call you on.';
  else if (digits.length < 10) errs.phone = 'That looks short. 10 digits please.';
  if (!f.email.trim()) errs.email = 'Please add your email.';
  else if (!/^\S+@\S+\.\S+$/.test(f.email)) errs.email = 'Check the email address.';
  if (!f.salary) errs.salary = 'Pick the salary you are aiming for.';
  return errs;
}

function LeadForm({ source }: { source: string }) {
  const uid = useId();
  const fieldId = (k: string) => `lead-${uid}-${k}`;
  const [form, setForm] = useState({ name: '', phone: '', email: '', salary: '' });
  // A field people never see: a bot that fills it gets the thank-you and nothing is sent
  const [trap, setTrap] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');

  const change = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(p => ({ ...p, [k]: e.target.value }));
    setErrors(p => ({ ...p, [k]: undefined as any }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateLead(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      document.getElementById(fieldId(Object.keys(errs)[0]))?.focus();
      return;
    }
    setStatus('submitting');
    if (trap) { setStatus('sent'); return; }
    const fields = { name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(), salary: form.salary };
    // Both: the Google Sheet (with its email to the admin) and the sales CRM.
    // Either one getting it is enough for the student.
    const [sheet, crm] = await Promise.allSettled([
      fetch(LEAD_FORM_SCRIPT_URL, { method: 'POST', mode: 'no-cors', body: new URLSearchParams({ ...fields, page: window.location.pathname, section: source }) }),
      fetch(CRM_LEAD_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ form: 'analyst', ...fields, website: trap }) })
        .then(r => r.json()).then(r => { if (r.ok === false) throw new Error(r.error); }),
    ]);
    if (sheet.status === 'rejected' && crm.status === 'rejected') { setStatus('error'); return; }
    trackLead(source);
    setStatus('sent');
  };

  const cardStyle: React.CSSProperties = { background: COLOR.paper, borderRadius: 22, boxShadow: '0 26px 50px -32px rgba(40,8,32,0.5)', border: '1px solid rgba(40,8,32,.1)' };
  const inputStyle: React.CSSProperties = { width: '100%', minHeight: 50, border: '1.5px solid rgba(40,8,32,.16)', borderRadius: 12, padding: '0 16px', fontSize: 16, fontFamily: FONT_BODY, outline: 'none', boxSizing: 'border-box', color: COLOR.ink };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: COLOR.inkFaint };
  const errorStyle: React.CSSProperties = { color: '#c4341a', fontSize: 13, margin: '4px 0 0' };

  if (status === 'sent') {
    return (
      <div className="analyst-lead-card" style={cardStyle}>
        <div style={{ textAlign: 'center', padding: '20px 8px' }}>
          <div style={{ width: 54, height: 54, borderRadius: '50%', background: COLOR.goldWash, display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5 10 17.5 19 7" stroke={COLOR.ink} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 22, letterSpacing: '-0.02em', color: COLOR.ink, margin: 0 }}>{FORM_COPY.doneHeading} {form.name.trim().split(' ')[0]}.</p>
          <p style={{ maxWidth: '34ch', margin: '8px auto 0', fontSize: 15, color: COLOR.inkSoft }}>{FORM_COPY.doneBody}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="analyst-lead-card" style={cardStyle}>
      <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(20px, 2.4vw, 24px)', letterSpacing: '-0.02em', lineHeight: 1.15, color: COLOR.ink, margin: 0 }}>{FORM_COPY.heading}</p>
      <p style={{ marginTop: 7, fontSize: 14.5, color: COLOR.inkSoft }}>{FORM_COPY.sub}</p>

      {status === 'error' && (
        <p role="alert" style={{ marginTop: 16, background: '#fdecec', color: '#8a2a17', border: '1px solid rgba(196,52,26,.28)', borderRadius: 12, padding: '12px 14px', fontSize: 13.5, lineHeight: 1.5 }}>
          <strong>{FORM_COPY.errorHeading}</strong> {FORM_COPY.errorBody}{' '}
          <a href={`tel:${PHONE}`} style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}>{PHONE}</a>.
        </p>
      )}

      <div aria-hidden="true" style={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor={fieldId('website')}>Leave this empty</label>
        <input id={fieldId('website')} name="website" type="text" tabIndex={-1} autoComplete="off" value={trap} onChange={e => setTrap(e.target.value)} />
      </div>

      <div style={{ display: 'grid', gap: 14, marginTop: 22 }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <label htmlFor={fieldId('name')} style={labelStyle}>Your name</label>
          <input id={fieldId('name')} name="name" type="text" autoComplete="name" placeholder="Ajay Krishna" value={form.name} onChange={change('name')} aria-invalid={!!errors.name} style={errors.name ? { ...inputStyle, borderColor: '#c4341a' } : inputStyle} />
          {errors.name && <p style={errorStyle} role="alert">{errors.name}</p>}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <label htmlFor={fieldId('phone')} style={labelStyle}>Phone number</label>
          <input id={fieldId('phone')} name="phone" type="tel" inputMode="numeric" autoComplete="tel" placeholder="98765 43210" value={form.phone} onChange={change('phone')} aria-invalid={!!errors.phone} style={errors.phone ? { ...inputStyle, borderColor: '#c4341a' } : inputStyle} />
          {errors.phone && <p style={errorStyle} role="alert">{errors.phone}</p>}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <label htmlFor={fieldId('email')} style={labelStyle}>Email</label>
          <input id={fieldId('email')} name="email" type="email" autoComplete="email" placeholder="you@email.com" value={form.email} onChange={change('email')} aria-invalid={!!errors.email} style={errors.email ? { ...inputStyle, borderColor: '#c4341a' } : inputStyle} />
          {errors.email && <p style={errorStyle} role="alert">{errors.email}</p>}
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          <legend style={labelStyle}>{FORM_COPY.salaryLabel}</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
            {FORM_COPY.salaryOptions.map(opt => (
              <button key={opt} type="button" onClick={() => { setForm(p => ({ ...p, salary: opt })); setErrors(p => ({ ...p, salary: undefined as any })); }}
                aria-pressed={form.salary === opt}
                style={{
                  flex: '1 1 auto', minHeight: 46, border: `1.5px solid ${form.salary === opt ? COLOR.ink : 'rgba(40,8,32,.16)'}`,
                  background: form.salary === opt ? COLOR.goldWash : COLOR.paper,
                  color: COLOR.ink, borderRadius: 999, padding: '0 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_DISPLAY,
                }}>
                {opt}
              </button>
            ))}
          </div>
          {errors.salary && <p style={errorStyle} role="alert">{errors.salary}</p>}
        </fieldset>
      </div>

      <button type="submit" disabled={status === 'submitting'} style={{
        width: '100%', minHeight: 54, marginTop: 20, background: COLOR.flame, color: '#fff', border: 'none', borderRadius: 999,
        padding: '0 28px', fontWeight: 700, fontSize: 16, cursor: status === 'submitting' ? 'wait' : 'pointer',
        fontFamily: FONT_BODY, opacity: status === 'submitting' ? 0.7 : 1,
      }}>
        {status === 'submitting' ? FORM_COPY.submitLabelBusy : FORM_COPY.submitLabel}
      </button>
      <p style={{ textAlign: 'center', color: COLOR.inkFaint, fontSize: 12.5, marginTop: 12 }}>{FORM_COPY.note}</p>
    </form>
  );
}

function LeadModal({ source, onClose }: { source: string; onClose: () => void }) {
  return (
    <div role="dialog" aria-modal="true" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(28,6,22,0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.97 }} style={{ width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: -14, right: -14, width: 32, height: 32, borderRadius: '50%', background: COLOR.paper, border: `1px solid ${COLOR.rule}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: COLOR.ink, zIndex: 1 }}>×</button>
        <LeadForm source={source} />
      </motion.div>
    </div>
  );
}

// ─── HEADER ─────────────────────────────────────────────────────────────────────
function AnalystHeader() {
  return (
    <header>
      <div className="analyst-header-inner" style={{ ...container, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <img src={logo} alt="Vista Tech" width={140} height={104} className="analyst-logo" style={{ width: 'auto', flexShrink: 0 }} />
        <CTAButton href={HERO.primaryCta.href} small>{HERO.primaryCta.label}</CTAButton>
      </div>
    </header>
  );
}

// ─── HERO ───────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section data-section="hero">
      <div style={container}>
        <div className="analyst-hero-copy" style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <p style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: COLOR.ink, background: COLOR.goldWash, padding: '8px 16px', borderRadius: 999, margin: '0 0 22px' }}>
            <Sparkles size={16} style={{ flexShrink: 0 }} aria-hidden="true" /> {HERO.badge}
          </p>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontWeight: 300, fontSize: 'clamp(30px, 5.8vw, 54px)', letterSpacing: '-0.025em', lineHeight: 1.1, maxWidth: '25ch', margin: 0 }}>
            <span style={{ display: 'block' }}>{HERO.headlineLight}</span>
            <span style={{ display: 'block', fontWeight: 600 }}>
              {HERO.headlineBold}<span style={accentUnderline}>{HERO.headlineAccent}</span>
            </span>
          </h1>
        </div>
      </div>
    </section>
  );
}

// ─── PLACEMENT ROSTER + VIDEO ───────────────────────────────────────────────────
function RosterMarquee() {
  const list = PLACEMENT.placed;
  return (
    <div className="analyst-roster-window" style={{ overflow: 'hidden', maskImage: 'linear-gradient(180deg, transparent, #000 9%, #000 91%, transparent)' }}>
      <div className="analyst-roster-track" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[...list, ...list].map((s, i) => (
          <div key={i} className="analyst-roster-card" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px 12px 12px', border: '1px solid rgba(40,8,32,.1)', borderRadius: 18, background: COLOR.paper, boxShadow: '0 10px 24px -18px rgba(40,8,32,0.45)' }}>
            <Avatar src={s.photo} name={s.name} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontFamily: FONT_DISPLAY, fontSize: 16.5, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.22, color: COLOR.ink }}>{s.role}</span>
              <span style={{ display: 'block', marginTop: 3, fontSize: 13.5, color: COLOR.inkSoft }}>{s.name}</span>
            </span>
            <span style={{ flexShrink: 0, fontSize: 13, fontWeight: 700, color: COLOR.ink, whiteSpace: 'nowrap', background: COLOR.goldWash, padding: '7px 12px', borderRadius: 999 }}>{s.pay}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlacementSection() {
  // No autoplay here on purpose: every browser silently blocks (or strips the
  // audio from) autoplay-with-sound, so the only way to guarantee this plays
  // with full volume is a direct click on the player itself — that's a real
  // user gesture, so the browser allows sound immediately, no workarounds needed.
  return (
    <section className="analyst-placement-section" style={{ position: 'relative', background: COLOR.paper }} data-section="hero">
      <div style={container}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: 40, alignItems: 'center' }} className="analyst-grid-2">
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLOR.inkFaint, marginBottom: 14 }}>{PLACEMENT.placedLabel}</p>
            <RosterMarquee />
          </div>
          <div style={{ borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 50px rgba(40,8,32,0.16)', aspectRatio: '16 / 9', background: COLOR.inkDeep }}>
            <MuxPlayer
              playbackId={PLACEMENT.playbackId}
              streamType="on-demand"
              loop={false}
              title={PLACEMENT.posterAlt}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 44 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
            <CTAButton href={PLACEMENT.primaryCta.href}>{PLACEMENT.primaryCta.label}</CTAButton>
          </div>
          <p lang="ta" style={{ fontFamily: FONT_TAMIL, color: COLOR.inkSoft, marginTop: 14, fontSize: 15, textAlign: 'center' }}>{PLACEMENT.tamil}</p>
        </div>
      </div>
    </section>
  );
}

// ─── JOURNEY TIMELINE ───────────────────────────────────────────────────────────
function JourneyTimeline() {
  const [active, setActive] = useState(0);
  const ref = (node: HTMLOListElement | null) => {
    if (!node) return;
    const items = Array.from(node.querySelectorAll('li'));
    if (items.length === 0) return;
    const seen = new Set<number>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const idx = Number((entry.target as HTMLElement).dataset.index);
        if (entry.isIntersecting) seen.add(idx); else seen.delete(idx);
      });
      if (seen.size > 0) setActive(Math.max(...Array.from(seen)));
    }, { rootMargin: '-45% 0px -40% 0px', threshold: 0 });
    items.forEach(el => observer.observe(el));
  };

  return (
    <section style={{ position: 'relative', background: COLOR.sand }} className="analyst-pad-52-80" data-section="journey">
      <div style={container}>
        <div style={sectionHead}>
          <SectionHeading maxWidthCh={20}>{JOURNEY.heading} <span style={accentUnderline}>{JOURNEY.headingAccent}</span></SectionHeading>
        </div>
        <ol ref={ref} style={{ listStyle: 'none', padding: 0, width: '100%', maxWidth: 720, margin: '36px auto 0', display: 'grid', gap: 18 }} className="analyst-journey-steps">
          {JOURNEY.steps.map((step, i) => {
            const reached = i <= active;
            const railLit = i < active;
            return (
              <li key={step.title} data-index={i} style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 16 }} className="analyst-journey-step">
                <div style={{ position: 'relative', paddingTop: 12 }} className="analyst-journey-marker">
                  <span aria-hidden="true" className={`analyst-journey-rail ${railLit ? 'is-lit' : ''} ${i === JOURNEY.steps.length - 1 ? 'is-last' : ''}`} />
                  <span style={{
                    position: 'relative', zIndex: 1, width: 44, height: 44, borderRadius: '50%',
                    display: 'grid', placeItems: 'center', fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 17, fontVariantNumeric: 'tabular-nums',
                    background: reached ? COLOR.flame : COLOR.paper, border: `1.5px solid ${reached ? COLOR.flame : 'rgba(40,8,32,.16)'}`, color: reached ? '#fff' : COLOR.ink,
                    transition: 'background .45s var(--ease,ease), border-color .45s ease, color .45s ease',
                  }}>{i + 1}</span>
                </div>
                <div style={{ minWidth: 0, padding: '22px 20px', borderRadius: 20, background: COLOR.paper, border: `1px solid rgba(40,8,32,${reached ? '.16' : '.1'})`, boxShadow: reached ? '0 22px 46px -26px rgba(40,8,32,0.6)' : '0 14px 30px -26px rgba(40,8,32,0.45)', transition: 'box-shadow .45s ease, border-color .45s ease' }}>
                  <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(17px, 2.2vw, 20px)', letterSpacing: '-0.02em', lineHeight: 1.2, color: COLOR.ink, margin: 0 }}>{step.title}</p>
                  <p style={{ maxWidth: '52ch', marginTop: 7, fontSize: 'clamp(14.5px, 1.8vw, 16px)', lineHeight: 1.55, color: COLOR.inkSoft }}>{step.body}</p>
                </div>
              </li>
            );
          })}
        </ol>
        <p style={{ width: '100%', maxWidth: 720, margin: '32px auto 0', padding: '18px 20px', borderRadius: 16, background: COLOR.paper, border: '1px solid rgba(40,8,32,.1)', textAlign: 'center', fontSize: 'clamp(15px, 2vw, 17px)', lineHeight: 1.5, color: COLOR.inkSoft }}>
          {JOURNEY.summary.lead} <b style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: COLOR.ink }}>{JOURNEY.summary.strong}</b>
        </p>
      </div>
    </section>
  );
}

// ─── CURRICULUM ─────────────────────────────────────────────────────────────────
function ModuleAccordionItem({ m, defaultOpen }: { m: typeof CURRICULUM.modules[number]; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <li>
      <details open={open} onToggle={e => setOpen((e.target as HTMLDetailsElement).open)} style={{ borderRadius: 18, background: open ? COLOR.paper : COLOR.sand, border: `1px solid ${open ? 'rgba(248,88,48,.28)' : 'rgba(40,8,32,.08)'}`, overflow: 'hidden' }}>
        <summary className="analyst-module-summary" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'start', gap: 14, padding: '16px 18px', cursor: 'pointer' }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: open ? COLOR.inkSoft : COLOR.inkFaint }}>{m.n}</span>
              <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: COLOR.inkFaint, opacity: 0.6 }} />
              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: COLOR.flame, fontVariantNumeric: 'tabular-nums' }}>{m.days}</span>
            </span>
            <span className="analyst-module-title" style={{ fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.25, color: COLOR.ink }}>{m.title}</span>
            <span style={{ marginTop: 2, color: COLOR.inkSoft, fontSize: 13.5, lineHeight: 1.5 }}>{m.outcome}</span>
          </span>
          <span aria-hidden="true" style={{ position: 'relative', flexShrink: 0, width: 20, height: 20, marginTop: 14, borderRadius: '50%', background: COLOR.flameWash }}>
            <span style={{ position: 'absolute', inset: '50% 5px auto', height: 2, borderRadius: 2, background: COLOR.flame }} />
            <span style={{ position: 'absolute', inset: '50% 5px auto', height: 2, borderRadius: 2, background: COLOR.flame, transform: open ? 'rotate(0)' : 'rotate(90deg)', opacity: open ? 0 : 1, transition: 'transform .2s ease, opacity .2s ease' }} />
          </span>
        </summary>
        <ul style={{ listStyle: 'none', margin: 0, padding: '0 18px 18px 46px', display: 'grid', gap: 9 }}>
          {m.topics.map(t => (
            <li key={t} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 10, fontSize: 14, lineHeight: 1.5, color: COLOR.inkSoft }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginTop: 4 }} aria-hidden="true"><path d="M4 12.5 9.5 18 20 6.5" stroke={COLOR.ink} strokeOpacity={0.45} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {t}
            </li>
          ))}
        </ul>
      </details>
    </li>
  );
}

function CurriculumSection() {
  return (
    <section className="analyst-pad-52-80" style={{ position: 'relative', background: COLOR.paper }} data-section="curriculum">
      <div style={container}>
        <div style={sectionHead}>
          <SectionHeading maxWidthCh={18}>{CURRICULUM.heading} <span style={accentUnderline}>{CURRICULUM.headingAccent}</span></SectionHeading>
          <SectionSub maxWidthCh={50}>{CURRICULUM.sub}</SectionSub>
          <p style={{ marginTop: 20, textAlign: 'center', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLOR.flame }}>{CURRICULUM.countLabel}</p>
        </div>

        <ul style={{ width: '100%', maxWidth: 820, margin: '18px auto 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CURRICULUM.modules.map((m, i) => <ModuleAccordionItem key={m.n} m={m} defaultOpen={i === 0} />)}
        </ul>

        <p style={{ marginTop: 46, textAlign: 'center', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLOR.inkFaint }}>{CURRICULUM.toolsLabel}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 18 }}>
          {CURRICULUM.tools.map(t => (
            <div key={t.name} style={{ flex: '0 1 92px', aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 6px', borderRadius: 14, border: '1px solid rgba(40,8,32,.1)', background: COLOR.paper, textAlign: 'center' }}>
              <span style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', padding: 5, borderRadius: 9, background: COLOR.goldWash }}>
                <t.Icon size={18} style={{ color: COLOR.ink }} aria-hidden="true" />
              </span>
              <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 11, lineHeight: 1.15, color: COLOR.ink, margin: 0 }}>{t.name}</p>
            </div>
          ))}
        </div>

        <p style={{ maxWidth: 780, margin: '34px auto 0', padding: '20px 22px', borderRadius: 18, background: COLOR.sand, textAlign: 'center', fontSize: 'clamp(15px, 2vw, 17.5px)', lineHeight: 1.5, color: COLOR.inkSoft }}>
          {CURRICULUM.outcome.lead} <b style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: COLOR.ink }}>{CURRICULUM.outcome.strong}</b>
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28 }}>
          <CTAButton href={CURRICULUM.cta.href}>{CURRICULUM.cta.label}</CTAButton>
        </div>
      </div>
    </section>
  );
}

// ─── STARTER WEEK ───────────────────────────────────────────────────────────────
function StarterWeek() {
  return (
    <section className="analyst-pad-52-80" style={{ position: 'relative', background: COLOR.sand, borderTop: `1px solid ${COLOR.rule}` }} data-section="starter-week">
      <div className="analyst-starter-grid" style={{ ...container, display: 'grid', gridTemplateColumns: '1fr', gridTemplateAreas: '"info" "video" "actions"', gap: 32 }}>
        <div style={{ gridArea: 'info' }}>
          <div style={{ marginBottom: 14 }}><SeatPill line="starter" offset={1} /></div>
          <p style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 15px', marginBottom: 16, borderRadius: 999, background: COLOR.paper, border: '1px solid rgba(40,8,32,.1)', fontSize: 13, fontWeight: 600, color: COLOR.ink }}>{STARTER_WEEK.badge}</p>
          <h2 style={{ maxWidth: '19ch', fontFamily: FONT_DISPLAY, fontWeight: 300, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em', lineHeight: 1.14, margin: 0 }}>
            <span style={{ color: COLOR.inkSoft }}>{STARTER_WEEK.headingLight}</span> <span style={accentUnderline}>{STARTER_WEEK.headingAccent}</span>
          </h2>
          <p style={{ maxWidth: '48ch', marginTop: 14, fontSize: 'clamp(15px, 1.8vw, 16.5px)', color: COLOR.inkSoft }}>{STARTER_WEEK.sub}</p>
          <div className="analyst-starter-benefits" style={{ listStyle: 'none', margin: '24px 0 0', padding: 0, display: 'grid', gap: 14 }}>
            {STARTER_WEEK.benefits.map(b => (
              <div key={b.title} style={{ display: 'grid', gridTemplateColumns: '38px 1fr', gap: 12 }}>
                <span style={{ width: 38, height: 38, padding: 8, borderRadius: 11, background: COLOR.paper, border: '1px solid rgba(40,8,32,.08)', color: COLOR.ink }}>
                  <b.Icon size={20} aria-hidden="true" />
                </span>
                <div>
                  <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 15.5, letterSpacing: '-0.015em', lineHeight: 1.25, color: COLOR.ink, margin: 0 }}>{b.title}</p>
                  <p style={{ marginTop: 3, fontSize: 14, lineHeight: 1.45, color: COLOR.inkSoft, margin: '3px 0 0' }}>{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ gridArea: 'video', maxWidth: 300, width: '100%', margin: '0 auto' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 400, marginInline: 'auto', aspectRatio: '9 / 16', borderRadius: 24, overflow: 'hidden', background: COLOR.inkDeep, boxShadow: '0 30px 60px -30px rgba(40,8,32,0.55)' }}>
            <iframe src={STARTER_WEEK.embedSrc} title={STARTER_WEEK.posterAlt} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen style={{ width: '100%', height: '100%', border: 0 }} />
          </div>
        </div>
        <div className="analyst-starter-actions" style={{ gridArea: 'actions', marginTop: 4 }}>
          <CTAButton href={STARTER_WEEK.cta.href}>{STARTER_WEEK.cta.label}</CTAButton>
          <p style={{ marginTop: 11, fontSize: 13, color: COLOR.inkFaint }}>{STARTER_WEEK.note}</p>
        </div>
      </div>
    </section>
  );
}

// ─── TRUST BAR ──────────────────────────────────────────────────────────────────
function MarqueeRow({ names, reverse }: { names: string[]; reverse?: boolean }) {
  return (
    <div style={{ overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent)' }}>
      <div className={reverse ? 'analyst-marquee-reverse' : 'analyst-marquee'} style={{ display: 'flex', gap: 12, width: 'max-content' }}>
        {[...names, ...names].map((n, i) => (
          <span key={i} style={{ flexShrink: 0, background: COLOR.paper, border: '1px solid rgba(40,8,32,.07)', borderRadius: 999, padding: '12px 22px', boxShadow: '0 8px 20px -16px rgba(40,8,32,0.5)', fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em', color: COLOR.ink, whiteSpace: 'nowrap' }}>{n}</span>
        ))}
      </div>
    </div>
  );
}

function TrustBar() {
  return (
    <section className="analyst-pad-48-72" style={{ position: 'relative', background: COLOR.sand }}>
      <div className="analyst-trust-split" style={{ ...container, display: 'grid', gap: 34, alignItems: 'start' }}>
        <div className="analyst-trust-head" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <h2 style={{ maxWidth: '22ch', fontFamily: FONT_DISPLAY, fontWeight: 300, fontSize: 'clamp(26px, 4.2vw, 40px)', letterSpacing: '-0.025em', lineHeight: 1.12, margin: 0 }}>
            Companies hiring through <b style={accentUnderline}>our placement partners</b>
          </h2>
          <p style={{ maxWidth: '54ch', marginTop: 14, fontSize: 'clamp(15px, 1.8vw, 16.5px)', color: COLOR.inkSoft }}>{TRUST.sub}</p>
          <div className="analyst-trust-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', alignItems: 'center', gap: 14, width: '100%', maxWidth: 620, marginTop: 30 }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(42px, 8.4vw, 72px)', letterSpacing: '-0.04em', lineHeight: 1, color: COLOR.ink, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ backgroundImage: `linear-gradient(${COLOR.gold}, ${COLOR.gold})`, backgroundRepeat: 'no-repeat', backgroundPosition: '0 96%', backgroundSize: '100% 0.1em' }}>{TRUST.stats[0].value}<span style={{ fontSize: '0.52em', letterSpacing: '-0.02em' }}>{TRUST.stats[0].suffix}</span></span>
                {TRUST.stats[0].star && <span style={{ fontSize: '0.38em', fontWeight: 700, color: COLOR.flame, verticalAlign: 'super' }}>*</span>}
              </p>
              <p style={{ marginTop: 10, fontSize: 'clamp(13px, 1.7vw, 15px)', lineHeight: 1.35, color: COLOR.inkSoft }}>{TRUST.stats[0].label}</p>
            </div>
            <span aria-hidden="true" style={{ alignSelf: 'stretch', background: 'rgba(40,8,32,.14)' }} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(42px, 8.4vw, 72px)', letterSpacing: '-0.04em', lineHeight: 1, color: COLOR.ink, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ backgroundImage: `linear-gradient(${COLOR.gold}, ${COLOR.gold})`, backgroundRepeat: 'no-repeat', backgroundPosition: '0 96%', backgroundSize: '100% 0.1em' }}>{TRUST.stats[1].value}<span style={{ fontSize: '0.52em', letterSpacing: '-0.02em' }}>{TRUST.stats[1].suffix}</span></span>
                {TRUST.stats[1].star && <span style={{ fontSize: '0.38em', fontWeight: 700, color: COLOR.flame, verticalAlign: 'super' }}>*</span>}
              </p>
              <p style={{ marginTop: 10, fontSize: 'clamp(13px, 1.7vw, 15px)', lineHeight: 1.35, color: COLOR.inkSoft }}>{TRUST.stats[1].label}</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 28, width: '100%' }}>
            <MarqueeRow names={TRUST.rowOne} />
            <MarqueeRow names={TRUST.rowTwo} reverse />
          </div>
          <p style={{ maxWidth: '58ch', marginTop: 20, fontSize: 12.5, lineHeight: 1.5, color: COLOR.inkFaint }}>{TRUST.footnote}</p>
        </div>
        <LeadForm source="companies-inline" />
      </div>
    </section>
  );
}

// ─── VALUE STACK ────────────────────────────────────────────────────────────────
function ValueStack() {
  return (
    <section className="analyst-pad-48-80" style={{ position: 'relative', background: COLOR.sand }} id="value-stack">
      <div style={container}>
        <div style={sectionHead}>
          <div style={{ marginBottom: 14 }}><SeatPill line="value" offset={0} /></div>
          <SectionHeading maxWidthCh={20} size="lg">{VALUE_STACK.heading} <span style={accentUnderline}>{VALUE_STACK.headingAccent}</span></SectionHeading>
          <p style={{ maxWidth: '48ch', marginTop: 13, fontSize: 15.5, lineHeight: 1.62, color: COLOR.inkSoft, textAlign: 'center' }}>{VALUE_STACK.sub}</p>
        </div>
        <div className="analyst-value-card" style={{ width: '100%', maxWidth: 680, margin: '26px auto 0', padding: '8px 18px 20px', borderRadius: 30, background: COLOR.paper, border: '1px solid rgba(40,8,32,.08)', boxShadow: '0 20px 44px -30px rgba(40,8,32,0.4)' }}>
          {VALUE_STACK.items.map((item, i) => (
            <div key={item.title} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: i < VALUE_STACK.items.length - 1 ? '1px solid rgba(40,8,32,.08)' : 'none' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span className="analyst-value-row-title" style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 600, letterSpacing: '-0.012em', lineHeight: 1.25, color: COLOR.ink }}>{item.title}</span>
                <span style={{ color: COLOR.inkSoft, fontSize: 13.5, lineHeight: 1.5 }}>{item.body}</span>
              </span>
              <span style={{ flexShrink: 0, color: COLOR.inkSoft, fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap' }}>{item.value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginTop: 4, paddingTop: 16, borderTop: '2px dashed rgba(40,8,32,.22)' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: COLOR.inkSoft }}>{VALUE_STACK.totalLabel}</span>
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: 'clamp(22px, 3.4vw, 28px)', fontWeight: 600, letterSpacing: '-0.03em', color: COLOR.ink }}>{VALUE_STACK.total}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 16, padding: '16px 18px', borderRadius: 18, background: COLOR.goldWash, border: '1px solid rgba(216,184,32,.4)' }}>
            <span style={{ maxWidth: '16ch', fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: COLOR.ink }}>{VALUE_STACK.feeLabel}</span>
            <span style={{ flexShrink: 0, fontFamily: FONT_DISPLAY, fontSize: 'clamp(30px, 5vw, 42px)', fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1, color: COLOR.ink, whiteSpace: 'nowrap' }}>{VALUE_STACK.fee}</span>
          </div>
          <p style={{ marginTop: 14, color: COLOR.money, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, textAlign: 'center' }}>{VALUE_STACK.note}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 28 }}>
          <div className="analyst-value-buttons" style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 340 }}>
            <CTAButton href={VALUE_STACK.cta.href}>{VALUE_STACK.cta.label}</CTAButton>
            <CTAButton href={VALUE_STACK.secondaryCta.href} variant="secondary">{VALUE_STACK.secondaryCta.label}</CTAButton>
          </div>
          <p style={{ maxWidth: '68ch', color: COLOR.inkFaint, fontSize: 12, lineHeight: 1.55, textAlign: 'center' }}>{VALUE_STACK.footnote}</p>
        </div>
      </div>
    </section>
  );
}

// ─── PRICING ────────────────────────────────────────────────────────────────────
function PlanCard({ plan, offset }: { plan: typeof PRICING.plans[number]; offset: number }) {
  return (
    <li className="analyst-plan-card" style={{
      display: 'flex', flexDirection: 'column', padding: '22px 20px 24px', borderRadius: 30,
      background: plan.featured ? COLOR.paper : COLOR.sand,
      border: plan.featured ? `2px solid ${COLOR.flame}` : '1px solid rgba(40,8,32,.08)',
      boxShadow: plan.featured ? '0 18px 40px -24px rgba(40,8,32,0.35)' : 'none',
    }}>
      {plan.featured && <span style={{ alignSelf: 'flex-start', padding: '5px 12px', marginBottom: 12, borderRadius: 999, background: COLOR.flame, color: COLOR.paper, fontSize: 12, fontWeight: 700 }}>{plan.featuredLabel}</span>}
      <div style={{ minHeight: 62 }}>
        <p style={{ fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 600, letterSpacing: '-0.018em', color: COLOR.ink, margin: 0 }}>{plan.name}</p>
        <p style={{ marginTop: 5, color: COLOR.inkSoft, fontSize: 13.5, lineHeight: 1.45 }}>{plan.note}</p>
      </div>
      <div style={{ marginTop: 12 }}><SeatPill line="plan" offset={offset} taken={plan.seats.taken} total={plan.seats.total} compact /></div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14, padding: '14px 16px', borderRadius: 18, background: COLOR.flameWash, border: '1px solid rgba(248,88,48,.2)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ color: COLOR.inkSoft, fontSize: 12.5, fontWeight: 600 }}>{PRICING.labels.now}</span>
          <span style={{ fontFamily: FONT_DISPLAY, fontSize: 'clamp(27px, 4.6vw, 33px)', fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.06, color: COLOR.flame, fontVariantNumeric: 'tabular-nums' }}>{plan.now}</span>
        </div>
        <span style={{ alignSelf: 'flex-start', padding: '2px 9px', borderRadius: 999, background: COLOR.paper, border: '1px solid rgba(248,88,48,.24)', color: COLOR.inkSoft, fontSize: 11.5, fontWeight: 600 }}>then</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ color: COLOR.inkSoft, fontSize: 12.5, fontWeight: 600 }}>{PRICING.labels.later}</span>
          <span style={{ fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 600, letterSpacing: '-0.018em', lineHeight: 1.15, color: COLOR.ink, opacity: 0.68 }}>{PRICING.labels.laterText}</span>
          <span style={{ marginTop: 3, color: COLOR.inkSoft, fontSize: 12.5, lineHeight: 1.45 }}>{PRICING.labels.laterNote}</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginTop: 16, paddingBottom: 14, borderBottom: '1px solid rgba(40,8,32,.1)' }}>
        <span style={{ color: COLOR.inkSoft, fontSize: 13, fontWeight: 600 }}>{PRICING.labels.total}</span>
        <span style={{ flexShrink: 0, fontFamily: FONT_DISPLAY, fontSize: 'clamp(25px, 3.6vw, 30px)', fontWeight: 600, letterSpacing: '-0.032em', color: COLOR.ink }}>{plan.total}</span>
      </div>

      <ul style={{ display: 'flex', flexDirection: 'column', gap: 9, margin: '14px 0 0', padding: 0, listStyle: 'none', flex: 1 }}>
        {plan.points.map(p => (
          <li key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 14, lineHeight: 1.45, color: COLOR.inkSoft }}>
            <span aria-hidden="true" style={{ flexShrink: 0, width: 14, height: 8, marginTop: 5, borderLeft: `2px solid ${COLOR.money}`, borderBottom: `2px solid ${COLOR.money}`, borderRadius: 1, transform: 'rotate(-45deg)' }} />
            {p}
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 20 }}>
        <CTAButton href={PRICING.cta.href} variant={plan.featured ? 'primary' : 'secondary'}>{PRICING.cta.label}</CTAButton>
      </div>
    </li>
  );
}

function PricingSection() {
  return (
    <section className="analyst-pad-52-84" style={{ position: 'relative', background: COLOR.paper }} id="fees" data-section="pricing">
      <div style={container}>
        <div style={sectionHead}>
          <p style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 15px', margin: '14px 0 16px', borderRadius: 999, background: COLOR.goldWash, border: '1px solid rgba(216,184,32,.34)', fontSize: 13, fontWeight: 600, color: COLOR.ink }}>{PRICING.badge}</p>
          <SectionHeading maxWidthCh={21} size="lg">{PRICING.heading} <span style={accentUnderline}>{PRICING.headingAccent}</span></SectionHeading>
          <p style={{ maxWidth: '50ch', marginTop: 14, color: COLOR.inkSoft, fontSize: 15.5, lineHeight: 1.62, textAlign: 'center' }}>{PRICING.sub}</p>
        </div>

        <ol className="analyst-beats" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, width: '100%', maxWidth: 940, margin: '30px auto 0', padding: 0, listStyle: 'none', borderRadius: 18, background: COLOR.rule, overflow: 'hidden' }}>
          {PRICING.beats.map(b => (
            <li key={b.step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', background: COLOR.sand }}>
              <span style={{ display: 'grid', placeItems: 'center', flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: COLOR.flame, color: COLOR.paper, fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 600 }}>{b.step}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <span style={{ fontFamily: FONT_DISPLAY, fontSize: 15.5, fontWeight: 600, letterSpacing: '-0.012em', color: COLOR.ink }}>{b.title}</span>
                <span style={{ color: COLOR.inkSoft, fontSize: 13, lineHeight: 1.5 }}>{b.body}</span>
              </span>
            </li>
          ))}
        </ol>

        <ul className="analyst-plans" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, margin: '18px 0 0', padding: 0, listStyle: 'none' }}>
          {PRICING.plans.map((p, i) => <PlanCard key={p.id} plan={p} offset={i === 0 ? 0 : 2} />)}
        </ul>

        <ul className="analyst-assurances" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, margin: '30px 0 0', padding: 0, listStyle: 'none' }}>
          {PRICING.assurances.map(a => (
            <li key={a.title} style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 14, borderLeft: `2px solid ${COLOR.gold}` }}>
              <span style={{ fontFamily: FONT_DISPLAY, fontSize: 15.5, fontWeight: 600, letterSpacing: '-0.012em', color: COLOR.ink }}>{a.title}</span>
              <span style={{ color: COLOR.inkSoft, fontSize: 13.5, lineHeight: 1.55 }}>{a.body}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ─── VIDEO TESTIMONIALS ─────────────────────────────────────────────────────────
// circular signed distance from `active` to `i`, e.g. wraps 0↔(len-1) so the stack
// fans out symmetrically instead of jumping the long way round.
function circularOffset(i: number, active: number, len: number) {
  let d = i - active;
  if (d > len / 2) d -= len;
  if (d < -len / 2) d += len;
  return d;
}

function LanyardCard({ person, offset, onFocus, isActive }: { person: typeof VIDEO_TESTIMONIALS.items[number]; offset: number; onFocus: () => void; isActive: boolean }) {
  const ad = Math.abs(offset);
  return (
    <div
      className="analyst-lanyard-item"
      style={{ '--d': offset, '--ad': ad, cursor: isActive ? 'default' : 'pointer' } as React.CSSProperties}
      onClick={!isActive ? onFocus : undefined}
      aria-hidden={!isActive}
    >
      <div aria-hidden="true" style={{ position: 'relative', height: 88, pointerEvents: 'none' }}>
        <span style={{
          position: 'absolute', bottom: 14, left: '50%', width: 21, height: 116, borderRadius: 2,
          background: `linear-gradient(90deg, ${COLOR.inkDeep} 0 34%, ${COLOR.gold} 34% 48%, #2f0f28 48% 100%)`,
          maskImage: 'linear-gradient(180deg, transparent 0%, #000 46%)', WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 46%)',
          transformOrigin: 'bottom center', transform: 'translateX(-50%) rotate(-25deg)',
        }} />
        <span style={{
          position: 'absolute', bottom: 14, left: '50%', width: 21, height: 116, borderRadius: 2,
          background: `linear-gradient(90deg, ${COLOR.inkDeep} 0 34%, ${COLOR.gold} 34% 48%, #2f0f28 48% 100%)`,
          maskImage: 'linear-gradient(180deg, transparent 0%, #000 46%)', WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 46%)',
          transformOrigin: 'bottom center', transform: 'translateX(-50%) rotate(25deg)',
        }} />
        <span style={{
          position: 'absolute', bottom: -5, left: '50%', transform: 'translateX(-50%)', width: 38, height: 20, borderRadius: 5,
          background: 'linear-gradient(180deg, #efeae1, #c3bbaf 45%, #a89f92 60%, #ddd6ca)', boxShadow: '0 2px 5px -1px rgba(40,8,32,0.35)',
        }} />
      </div>
      <div style={{ borderRadius: 22, background: COLOR.paper, border: '1px solid rgba(40,8,32,.1)', boxShadow: '0 26px 50px -30px rgba(40,8,32,0.55)', overflow: 'hidden' }}>
        <div aria-hidden="true" style={{ height: 7, background: `linear-gradient(90deg, ${COLOR.flame} 0 38%, ${COLOR.gold} 38% 62%, ${COLOR.ink} 62% 100%)` }} />
        <div style={{ position: 'relative', aspectRatio: '4 / 5', background: COLOR.inkDeep }}>
          {isActive
            ? <video src={person.videoSrc} poster={person.poster} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <img src={person.poster} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
        <p style={{ marginTop: 12, textAlign: 'center', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: COLOR.ink }}>{person.company}</p>
        <div style={{ padding: '16px 18px 0', textAlign: 'center' }}>
          <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 21, letterSpacing: '-0.02em', lineHeight: 1.15, color: COLOR.ink, margin: 0 }}>{person.name}</p>
          <p style={{ marginTop: 4, fontSize: 14.5, lineHeight: 1.4, color: COLOR.inkSoft }}>{person.role}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '4px 18px 0', paddingTop: 14, borderTop: '1px dashed rgba(40,8,32,.18)' }}>
          <div style={{ textAlign: 'left' }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLOR.inkFaint, margin: 0 }}>Background</p>
            <p style={{ marginTop: 3, fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 15, color: COLOR.ink, margin: '3px 0 0' }}>{person.background}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLOR.inkFaint, margin: 0 }}>Package</p>
            <p style={{ marginTop: 3, fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 15, color: COLOR.ink, margin: '3px 0 0' }}>{person.pay}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 18px 16px' }}>
          <span aria-hidden="true" style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 22 }}>
            {[8, 14, 20, 14, 8].map((h, i) => <span key={i} style={{ display: 'block', width: 2, height: h, background: COLOR.ink, opacity: 0.8 }} />)}
          </span>
          <span style={{ fontSize: 10.5, color: COLOR.inkFaint, textAlign: 'right', lineHeight: 1.3 }}>Trained at<br />Vista Tech</span>
        </div>
      </div>
    </div>
  );
}

function VideoTestimonials() {
  const [index, setIndex] = useState(0);
  const items = VIDEO_TESTIMONIALS.items;
  const move = (d: number) => setIndex(i => (i + d + items.length) % items.length);

  return (
    <section className="analyst-pad-52-78" style={{ position: 'relative', background: COLOR.paper }}>
      <div style={container}>
        <div style={sectionHead}>
          <SectionHeading maxWidthCh={24}>{VIDEO_TESTIMONIALS.heading} <span style={accentUnderline}>{VIDEO_TESTIMONIALS.headingAccent}</span></SectionHeading>
        </div>

        <div className="analyst-lanyard-stage">
          {items.map((person, i) => (
            <LanyardCard key={person.id} person={person} offset={circularOffset(i, index, items.length)} isActive={i === index} onFocus={() => setIndex(i)} />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 28 }}>
          <button onClick={() => move(-1)} aria-label="Previous student" style={{ width: 46, height: 46, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', border: '1.5px solid rgba(40,8,32,.18)', background: COLOR.paper, color: COLOR.ink, cursor: 'pointer' }}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <span style={{ display: 'flex', gap: 8 }}>
            {items.map((it, i) => (
              <button key={it.id} onClick={() => setIndex(i)} aria-label={`Show ${it.name}`} aria-current={i === index}
                style={{ width: i === index ? 22 : 8, height: 8, borderRadius: 999, border: 'none', cursor: 'pointer', background: i === index ? COLOR.flame : 'rgba(40,8,32,.2)', transition: 'width .2s ease, background .2s ease' }} />
            ))}
          </span>
          <button onClick={() => move(1)} aria-label="Next student" style={{ width: 46, height: 46, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', border: '1.5px solid rgba(40,8,32,.18)', background: COLOR.paper, color: COLOR.ink, cursor: 'pointer' }}>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── TEXT TESTIMONIALS ──────────────────────────────────────────────────────────
function TextTestimonials() {
  return (
    <section className="analyst-pad-52-80" style={{ position: 'relative', background: COLOR.sand }}>
      <div style={container}>
        <div style={sectionHead}>
          <SectionHeading maxWidthCh={18}>{TEXT_TESTIMONIALS.heading} <span style={accentUnderline}>{TEXT_TESTIMONIALS.headingAccent}</span></SectionHeading>
          <SectionSub maxWidthCh={46}>{TEXT_TESTIMONIALS.sub}</SectionSub>
        </div>
        <div className="analyst-testimonial-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, marginTop: 34 }}>
          {TEXT_TESTIMONIALS.items.map((t, i) => (
            <article key={t.name} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '22px 20px', borderRadius: 18, background: COLOR.paper, border: '1px solid rgba(40,8,32,.09)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', background: AVATAR_PALETTE[i % AVATAR_PALETTE.length], fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 16, color: COLOR.ink }}>{t.name.trim().charAt(0)}</span>
                <div>
                  <p style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 15.5, letterSpacing: '-0.01em', lineHeight: 1.2, color: COLOR.ink, margin: 0 }}>{t.name}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                    <StarRow rating={t.rating} color="#e8a317" />
                    <span style={{ color: COLOR.inkFaint, fontSize: 12.5 }}>{t.time}</span>
                  </div>
                </div>
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: COLOR.inkSoft, margin: 0 }}>{t.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── REAL STUDENTS TABLE ────────────────────────────────────────────────────────
function StudentsTable() {
  return (
    <section className="analyst-pad-52-80" style={{ position: 'relative', background: COLOR.paper }}>
      <div style={container}>
        <div style={sectionHead}>
          <SectionHeading maxWidthCh={20}>{STUDENTS_TABLE.heading} <span style={accentUnderline}>{STUDENTS_TABLE.headingAccent}</span></SectionHeading>
          <SectionSub maxWidthCh={46}>{STUDENTS_TABLE.sub}</SectionSub>
        </div>
        <div style={{ maxWidth: 900, margin: '32px auto 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '40px 1.1fr 1.1fr 1fr auto', gap: 16, padding: '0 18px 10px', borderBottom: '1px solid rgba(40,8,32,.12)' }} className="analyst-table-head">
            {STUDENTS_TABLE.columns.map((c, i) => <span key={c} style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: COLOR.inkFaint, textAlign: i === STUDENTS_TABLE.columns.length - 1 ? 'right' : 'left' }}>{c}</span>)}
          </div>
          <div className="analyst-table-window" style={{ overflow: 'hidden', maskImage: 'linear-gradient(180deg, transparent, #000 7%, #000 92%, transparent)', WebkitMaskImage: 'linear-gradient(180deg, transparent, #000 7%, #000 92%, transparent)' }}>
            <ul className="analyst-table-track" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {[...STUDENTS_TABLE.students, ...STUDENTS_TABLE.students].map((s, i) => (
                <li key={i} style={{ display: 'grid', gridTemplateColumns: '40px 1.1fr 1.1fr 1fr auto', gap: 16, alignItems: 'center', padding: '12px 18px', borderRadius: 12, background: i % 2 === 0 ? COLOR.paper : COLOR.sand }} className="analyst-table-row">
                  <Avatar src={s.photo} name={s.name} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 15.5, letterSpacing: '-0.015em', lineHeight: 1.25, color: COLOR.ink }}>{s.name}</span>
                    {s.edu && <span style={{ display: 'block', marginTop: 1, fontSize: 12.5, color: COLOR.inkFaint }}>{s.edu}</span>}
                  </span>
                  <span style={{ fontSize: 14.5, color: COLOR.ink }}>{s.role}</span>
                  <span style={{ fontSize: 14, color: COLOR.inkSoft }}>{s.company}</span>
                  <span style={{ justifySelf: 'end', fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 14.5, color: COLOR.ink, background: COLOR.goldWash, padding: '5px 11px', borderRadius: 999, whiteSpace: 'nowrap' }}>{s.salary}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ────────────────────────────────────────────────────────────────────────
function FAQSection() {
  const [open, setOpen] = useState<Set<number>>(new Set([0, 1]));
  const toggle = (i: number) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  return (
    <section className="analyst-pad-46-78" style={{ position: 'relative', background: COLOR.sand }} id="faq">
      <div style={container}>
        <h2 style={{ maxWidth: '22ch', margin: '0 auto', textAlign: 'center', fontFamily: FONT_DISPLAY, fontWeight: 300, fontSize: 'clamp(28px, 4.6vw, 44px)', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
          {FAQ.heading} <span style={accentUnderline}>{FAQ.headingAccent}</span>
        </h2>
        <div style={{ width: '100%', maxWidth: 740, margin: '28px auto 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQ.items.map((f, i) => {
            const isOpen = open.has(i);
            return (
              <div key={f.q} style={{ borderRadius: 18, background: COLOR.paper, border: `1px solid ${isOpen ? 'rgba(248,88,48,.28)' : 'rgba(40,8,32,.08)'}`, overflow: 'hidden' }}>
                <button onClick={() => toggle(i)} aria-expanded={isOpen}
                  style={{ width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, padding: '16px 18px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: FONT_DISPLAY, fontSize: 16.5, fontWeight: 600, letterSpacing: '-0.012em', lineHeight: 1.32, color: COLOR.ink }}>
                  <span>{f.q}</span>
                  <span aria-hidden="true" style={{ position: 'relative', flexShrink: 0, width: 20, height: 20, marginTop: 2, borderRadius: '50%', background: COLOR.flameWash }}>
                    <span style={{ position: 'absolute', inset: '50% 5px auto', height: 2, borderRadius: 2, background: COLOR.flame }} />
                    <span style={{ position: 'absolute', inset: '50% 5px auto', height: 2, borderRadius: 2, background: COLOR.flame, transform: isOpen ? 'rotate(0)' : 'rotate(90deg)', opacity: isOpen ? 0 : 1, transition: 'transform .2s ease, opacity .2s ease' }} />
                  </span>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
                      <p style={{ padding: '0 18px 18px', color: COLOR.inkSoft, fontSize: 14.5, lineHeight: 1.62, margin: 0 }}>{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── CLOSING CTA + FOOTER ───────────────────────────────────────────────────────
function ClosingSection() {
  return (
    <section id="enroll" className="analyst-pad-56-96" style={{ position: 'relative', overflow: 'hidden', background: COLOR.paper }} data-section="closing">
      <div style={{ ...container, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}><SeatPill line="closing" offset={3} /></div>
        <h2 style={{ maxWidth: '26ch', fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(30px, 5.4vw, 54px)', letterSpacing: '-0.038em', lineHeight: 1.04, margin: 0, color: COLOR.ink }}>{CLOSING.heading}</h2>
        <p style={{ maxWidth: '50ch', marginTop: 18, fontSize: 16, lineHeight: 1.6, color: COLOR.inkSoft }}>{CLOSING.body}</p>

        <div className="analyst-recap" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, width: '100%', maxWidth: 620, margin: '26px 0 0', borderRadius: 18, background: COLOR.rule, overflow: 'hidden' }}>
          {CLOSING.recap.map(r => (
            <div key={r.big} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '14px 16px', background: COLOR.sand }}>
              <p style={{ fontFamily: FONT_DISPLAY, fontSize: 'clamp(19px, 2.6vw, 23px)', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.1, margin: 0, color: COLOR.ink }}>{r.big}</p>
              <p style={{ color: COLOR.inkSoft, fontSize: 12.5, lineHeight: 1.4, margin: 0 }}>{r.small}</p>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12, width: '100%', maxWidth: 340, marginTop: 28 }} className="analyst-closing-buttons">
          <CTAButton href={CLOSING.cta.href}>{CLOSING.cta.label}</CTAButton>
        </div>
        <p style={{ maxWidth: '46ch', marginTop: 18, color: COLOR.inkFaint, fontSize: 13, lineHeight: 1.5 }}>{CLOSING.note}</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 20px', margin: '22px 0 0', padding: '22px 0 0', borderTop: `1px solid ${COLOR.rule}`, width: '100%', maxWidth: 720 }}>
          {CLOSING.trust.map(t => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: COLOR.inkSoft, fontSize: 12.5, lineHeight: 1.4 }}>
              <span aria-hidden="true" style={{ flexShrink: 0, width: 11, height: 6, marginTop: -3, borderLeft: `2px solid ${COLOR.money}`, borderBottom: `2px solid ${COLOR.money}`, borderRadius: 1, transform: 'rotate(-45deg)' }} />
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function AnalystFooter() {
  return (
    <footer style={{ position: 'relative', background: COLOR.sand, borderTop: `1px solid ${COLOR.rule}`, padding: '28px 0 32px' }}>
      <div style={{ ...container, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
        <p style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: COLOR.ink, margin: 0 }}>
          <PhoneIcon size={16} style={{ flexShrink: 0 }} aria-hidden="true" />{FOOTER_INFO.line}
        </p>
        <p style={{ color: COLOR.inkSoft, fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>
          {FOOTER_INFO.address}
          <span aria-hidden="true" style={{ padding: '0 7px', color: COLOR.inkFaint }}>·</span>
          <a href={`tel:${FOOTER_INFO.phone}`} style={{ color: 'inherit', borderBottom: '1px solid rgba(40,8,32,.22)' }}>{FOOTER_INFO.phone}</a>
          <span aria-hidden="true" style={{ padding: '0 7px', color: COLOR.inkFaint }}>·</span>
          <a href={`mailto:${FOOTER_INFO.email}`} style={{ color: 'inherit', borderBottom: '1px solid rgba(40,8,32,.22)' }}>{FOOTER_INFO.email}</a>
        </p>
        <p style={{ maxWidth: '58ch', marginTop: 4, color: COLOR.inkFaint, fontSize: 11.5, lineHeight: 1.5 }}>
          {FOOTER_INFO.links.map((l, i) => (
            <span key={l.label}>
              {i > 0 && <span aria-hidden="true" style={{ padding: '0 7px' }}>·</span>}
              <a href={l.href} style={{ color: 'inherit', borderBottom: '1px solid rgba(40,8,32,.22)' }}>{l.label}</a>
            </span>
          ))}
        </p>
      </div>
    </footer>
  );
}

// ─── GLOBAL KEYFRAMES ───────────────────────────────────────────────────────────
const GLOBAL_STYLE = `
@keyframes analyst-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes analyst-marquee-reverse { from { transform: translateX(-50%); } to { transform: translateX(0); } }
@keyframes analyst-roster-scroll { from { transform: translateY(0); } to { transform: translateY(-50%); } }
@keyframes analyst-pulse { 0% { opacity: .55; transform: scale(1); } 100% { opacity: 0; transform: scale(2.6); } }
.analyst-marquee { animation: analyst-marquee 28s linear infinite; }
.analyst-marquee-reverse { animation: analyst-marquee-reverse 32s linear infinite; }
.analyst-roster-track { animation: analyst-roster-scroll 22s linear infinite; }
.analyst-page details > summary { list-style: none; }
.analyst-page details > summary::-webkit-details-marker { display: none; }

/* section padding-block pairs, matching the live site's breakpoints exactly */
.analyst-pad-52-80 { padding-block: 52px; } @media (min-width: 900px) { .analyst-pad-52-80 { padding-block: 80px; } }
.analyst-pad-48-72 { padding-block: 48px; } @media (min-width: 900px) { .analyst-pad-48-72 { padding-block: 72px; } }
.analyst-pad-48-80 { padding-block: 48px; } @media (min-width: 900px) { .analyst-pad-48-80 { padding-block: 80px; } }
.analyst-pad-52-84 { padding-block: 52px; } @media (min-width: 900px) { .analyst-pad-52-84 { padding-block: 84px; } }
.analyst-pad-46-78 { padding-block: 46px; } @media (min-width: 900px) { .analyst-pad-46-78 { padding-block: 78px; } }
.analyst-pad-56-96 { padding-block: 56px; } @media (min-width: 900px) { .analyst-pad-56-96 { padding-block: 96px; } }
.analyst-pad-52-78 { padding-block: 52px; } @media (min-width: 900px) { .analyst-pad-52-78 { padding-block: 78px; } }

.analyst-hero-copy { padding-block: 20px 30px; } @media (min-width: 900px) { .analyst-hero-copy { padding-block: 28px 36px; } }
.analyst-header-inner { padding-block: 12px; } @media (min-width: 700px) { .analyst-header-inner { padding-block: 14px; gap: 14px; } }
.analyst-logo { height: 42px; } @media (min-width: 700px) { .analyst-logo { height: 46px; } }
.analyst-placement-section { padding-block: 0 52px; } @media (min-width: 900px) { .analyst-placement-section { padding-block: 0 76px; } }

.analyst-roster-window { --card-h: 92px; height: calc(var(--card-h) * 2 + 12px); }
@media (min-width: 700px) { .analyst-roster-window { --card-h: 94px; height: calc(var(--card-h) * 3 + 24px); } }
.analyst-roster-card { height: var(--card-h); }

.analyst-journey-step { grid-template-columns: 44px 1fr; }
@media (min-width: 700px) { .analyst-journey-step { grid-template-columns: 52px 1fr !important; gap: 20px !important; } }
.analyst-journey-rail { content: ""; position: absolute; left: 22px; top: 56px; bottom: -18px; width: 2px; transform: translateX(-50%); background: rgba(40,8,32,.16); transition: background .45s ease; }
.analyst-journey-rail.is-lit { background: var(--flame, #f85830); }
.analyst-journey-rail.is-last { display: none; }
@media (min-width: 700px) { .analyst-journey-rail { left: 26px; top: 66px; bottom: -20px; } }

.analyst-module-summary { padding: 16px 18px; }
@media (min-width: 700px) { .analyst-module-summary { padding: 18px 22px !important; gap: 16px !important; } .analyst-module-title { font-size: 19px !important; } }

.analyst-starter-grid { grid-template-areas: "info" "video" "actions"; }
@media (min-width: 900px) { .analyst-starter-grid { grid-template-columns: .82fr 1.18fr !important; grid-template-areas: "video info" "video actions" !important; gap: 52px !important; align-items: center; } }
.analyst-starter-benefits { grid-template-columns: 1fr; }
@media (min-width: 620px) { .analyst-starter-benefits { grid-template-columns: 1fr 1fr !important; gap: 16px 22px !important; } }
@media (max-width: 600px) { .analyst-starter-actions { text-align: center; } }

.analyst-trust-split { grid-template-columns: minmax(0, 1fr); }
@media (min-width: 940px) { .analyst-trust-split { grid-template-columns: minmax(0, 1.08fr) minmax(0, .92fr) !important; gap: 52px !important; } .analyst-trust-head { align-items: flex-start !important; text-align: left !important; } }

.analyst-value-card { padding: 8px 18px 20px; }
@media (min-width: 700px) { .analyst-value-card { padding: 14px 30px 26px !important; margin-top: 38px !important; } .analyst-value-row-title { font-size: 17.5px !important; } }
.analyst-value-buttons { flex-direction: column; }
@media (min-width: 601px) { .analyst-value-buttons { flex-direction: row !important; width: auto !important; max-width: none !important; gap: 14px !important; } }

.analyst-beats { grid-template-columns: 1fr; }
@media (min-width: 760px) { .analyst-beats { grid-template-columns: repeat(3, 1fr) !important; margin-top: 40px !important; } }
.analyst-plans { grid-template-columns: 1fr; }
@media (min-width: 760px) { .analyst-plans { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; max-width: 780px; margin: 26px auto 0 !important; } }
.analyst-assurances { grid-template-columns: 1fr; }
@media (min-width: 760px) { .analyst-assurances { grid-template-columns: repeat(3, 1fr) !important; gap: 22px !important; margin-top: 44px !important; } }

.analyst-testimonial-grid { grid-template-columns: 1fr; }
@media (min-width: 680px) { .analyst-testimonial-grid { grid-template-columns: 1fr 1fr !important; } }
@media (min-width: 1000px) { .analyst-testimonial-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 18px !important; margin-top: 46px !important; } }

.analyst-recap { grid-template-columns: 1fr; }
@media (min-width: 600px) { .analyst-recap { grid-template-columns: repeat(3, 1fr) !important; } }
.analyst-closing-buttons { width: 100%; max-width: 340px; }
@media (min-width: 601px) { .analyst-closing-buttons { width: auto !important; max-width: none !important; } }

.analyst-lead-card { padding: 24px 20px; }
@media (min-width: 700px) { .analyst-lead-card { padding: 30px 28px !important; } }

/* lanyard coverflow: every card sits in the same grid cell, positioned purely by
   the --d (signed distance from active) / --ad (absolute distance) custom props */
.analyst-lanyard-stage { --card-w: min(94vw, 360px); --spread: 0%; --fade: 1; position: relative; display: grid; justify-items: center; margin-top: 32px; padding-inline: 12px; }
@media (min-width: 900px) { .analyst-lanyard-stage { --card-w: 340px; --spread: 56%; --fade: .58; margin-top: 48px; } }
.analyst-lanyard-item {
  grid-area: 1 / 1;
  width: var(--card-w);
  transform: translate(calc(var(--d) * var(--spread)), calc(var(--ad) * 12px)) scale(calc(1 - var(--ad) * .15));
  opacity: calc(1 - var(--ad) * var(--fade));
  z-index: calc(10 - var(--ad));
  transition: transform .55s cubic-bezier(.2,.7,.3,1), opacity .55s cubic-bezier(.2,.7,.3,1);
}

/* real-students table: fixed-height window, auto-scrolling track (doubled rows loop) */
@keyframes analyst-table-scroll { from { transform: translateY(0); } to { transform: translateY(-50%); } }
.analyst-table-window { height: 420px; } @media (min-width: 820px) { .analyst-table-window { height: 362px; } }
.analyst-table-track { animation: analyst-table-scroll 46s linear infinite; display: flex; flex-direction: column; gap: 2px; }
.analyst-table-window:hover .analyst-table-track { animation-play-state: paused; }

@media (max-width: 820px) {
  .analyst-grid-2 { grid-template-columns: 1fr !important; }
  .analyst-table-head { display: none !important; }
  .analyst-table-row { grid-template-columns: 1fr !important; gap: 4px !important; }
}
`;

// ─── PAGE ───────────────────────────────────────────────────────────────────────
export default function Analyst() {
  useSEO();
  useAnalytics();
  const [modalSource, setModalSource] = useState<string | null>(null);

  return (
    <ModalCtx.Provider value={{ open: (source) => setModalSource(source) }}>
      <div className="analyst-page" style={{ background: COLOR.sand, color: COLOR.ink, fontFamily: FONT_BODY, minHeight: '100vh' }}>
        <style>{GLOBAL_STYLE}</style>
        <AnalystHeader />
        <main>
          <Hero />
          <PlacementSection />
          <TrustBar />
          <VideoTestimonials />
          <JourneyTimeline />
          <CurriculumSection />
          <TextTestimonials />
          <StudentsTable />
          <ValueStack />
          <StarterWeek />
          <PricingSection />
          <FAQSection />
          <ClosingSection />
        </main>
        <AnalystFooter />

        <AnimatePresence>
          {modalSource && <LeadModal source={modalSource} onClose={() => setModalSource(null)} />}
        </AnimatePresence>
      </div>
    </ModalCtx.Provider>
  );
}
